import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime, StatusBadge, StatusTone, formatDate, formatDateTime } from '../../../components/design-system/admin';
import { useAdminSignupsApi } from '../../../common/hooks/use-admin-signups-api.hook';
import { SIGNUP_STEPS, SdkPresence, SdkPresenceStatus, SignupDetail, SignupRow, SignupRowStatus, SignupStep, Verdict } from '../../../common/models/admin-signups.model';

export const STEP_KEY: Record<SignupStep, string> = {
  website: 'WEBSITE',
  account: 'ACCOUNT',
  emailVerified: 'EMAIL_VERIFIED',
  sdkConnected: 'SDK_CONNECTED',
  reviewRequested: 'REVIEW_REQUESTED',
  decided: 'DECIDED',
  onboarded: 'ONBOARDED',
};

export const VERDICT_TONE: Record<Verdict, StatusTone> = { business: 'success', unclear: 'warning', not_business: 'danger' };

export const STATUS_TONE: Record<SignupRowStatus, StatusTone> = {
  visitor: 'neutral',
  pending_embed: 'neutral',
  pending_review: 'warning',
  active: 'success',
  blocked: 'danger',
};

/** Translation-key suffix for an API enum ("not_business" → "NOT_BUSINESS"). */
export const enumKey = (v?: string | null) => String(v || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_');

/** The row's status in a word, with its dot. */
export const SignupStatusBadge = ({ status, className }: { status: SignupRowStatus; className?: string }) => {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'} className={className}>
      {t(`ADMIN_SIGNUPS.STATUS.${enumKey(status)}`)}
    </StatusBadge>
  );
};

/** "Looks like a business · 78" — the evidence's verdict, coloured only by what it means. */
export const VerdictChip = ({ verdict, className }: { verdict?: SignupRow['verdict']; className?: string }) => {
  const { t } = useTranslation();
  if (!verdict) return <span className={cn('text-xs text-muted-foreground', className)}>{t('ADMIN_SIGNUPS.VERDICT.NONE')}</span>;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5', className)} data-testid="verdict-chip">
      <StatusBadge tone={VERDICT_TONE[verdict.verdict] ?? 'neutral'}>{t(`ADMIN_SIGNUPS.VERDICT.${enumKey(verdict.verdict)}`)}</StatusBadge>
      <span className="text-xs text-muted-foreground tabular-nums">{verdict.score}</span>
    </span>
  );
};

/**
 * The seven milestones as dots — filled when reached, hollow when not; the
 * decision dot carries the decision's colour. Dates on hover.
 */
export const SignupProgress = ({ row }: { row: Pick<SignupRow, 'steps' | 'review' | 'status'> }) => {
  const { t } = useTranslation();
  const steps = row.steps || {};
  const decidedTone = row.review?.status === 'blocked' || row.status === 'blocked' ? 'bg-destructive border-destructive' : 'bg-success border-success';
  const reached = SIGNUP_STEPS.filter((s) => steps[s]).length;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 py-1"
            role="img"
            aria-label={t('ADMIN_SIGNUPS.TABLE.PROGRESS_LABEL', { done: reached, total: SIGNUP_STEPS.length })}
            data-testid="signup-progress"
          >
            {SIGNUP_STEPS.map((s) => (
              <span
                key={s}
                data-step={s}
                data-done={steps[s] ? 'true' : 'false'}
                className={cn(
                  'size-2 rounded-full border',
                  steps[s] ? (s === 'decided' ? decidedTone : 'border-foreground bg-foreground') : 'border-muted-foreground/40',
                )}
              />
            ))}
          </span>
        </TooltipTrigger>
        <TooltipContent className="p-2.5">
          <ol className="grid gap-1 text-xs">
            {SIGNUP_STEPS.map((s) => (
              <li key={s} className="flex justify-between gap-4">
                <span>{t(`ADMIN_SIGNUPS.STEP.${STEP_KEY[s]}`)}</span>
                <span className="tabular-nums opacity-80">{steps[s] ? formatDateTime(steps[s]) : t('ADMIN_SIGNUPS.STEP.NOT_YET')}</span>
              </li>
            ))}
          </ol>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/** "45 min" / "3 h" — a review time, roughly. */
export const useDuration = () => {
  const { t } = useTranslation();
  return (minutes: number | null | undefined) => {
    if (minutes == null) return '—';
    if (minutes < 60) return t('ADMIN_SIGNUPS.DURATION.MINUTES', { count: Math.max(1, Math.round(minutes)) });
    return t('ADMIN_SIGNUPS.DURATION.HOURS', { count: Math.round(minutes / 6) / 10 });
  };
};

export const SDK_TONE: Record<SdkPresenceStatus, StatusTone> = { live: 'success', quiet: 'warning', removed: 'danger', never: 'neutral' };

/** "Live" / "Quiet since 12 Oct" / "Removed" / "Not installed" — is the snippet still on the site. */
export const SdkPresenceBadge = ({ presence, className }: { presence?: SdkPresence | null; className?: string }) => {
  const { t } = useTranslation();
  if (!presence) return <span className={cn('text-xs text-muted-foreground', className)}>—</span>;
  const label =
    presence.status === 'quiet'
      ? t('ADMIN_SIGNUPS.SDK.QUIET', { date: formatDate(presence.lastSeenAt) })
      : t(`ADMIN_SIGNUPS.SDK.${enumKey(presence.status)}`);
  return (
    <span className={cn('inline-flex w-fit items-center rounded-md border px-1.5 py-0.5', className)} data-testid="sdk-presence" data-status={presence.status}>
      <StatusBadge tone={SDK_TONE[presence.status] ?? 'neutral'}>{label}</StatusBadge>
    </span>
  );
};

/** The presence, when the SDK was last seen, and a "Check now" button. */
export const SdkPresenceSummary = ({
  presence,
  checking,
  onCheck,
  testId,
}: {
  presence?: SdkPresence | null;
  checking?: boolean;
  onCheck?: () => void;
  testId?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <SdkPresenceBadge presence={presence} />
        {presence?.lastSeenAt && (
          <span className="text-xs whitespace-nowrap text-muted-foreground" data-testid="sdk-last-seen">
            {t('ADMIN_SIGNUPS.SDK.SEEN')} <RelativeTime value={presence.lastSeenAt} />
          </span>
        )}
      </div>
      {onCheck && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('ADMIN_SIGNUPS.SDK.CHECK_NOW')}
                disabled={checking}
                onClick={(e) => {
                  e.stopPropagation();
                  onCheck();
                }}
                data-testid={testId}
              >
                <RefreshCw className={checking ? 'animate-spin' : undefined} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('ADMIN_SIGNUPS.SDK.CHECK_NOW')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

/** "Check now": run the live check, say what it found, answer the fresh detail (or null). */
export const useSdkCheck = () => {
  const { t } = useTranslation();
  const api = useAdminSignupsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  return useCallback(async (vendorId: string): Promise<SignupDetail | null> => {
    try {
      const detail = await apiRef.current.sdkCheck(vendorId);
      const check = detail?.sdkPresence?.lastCheck;
      if (check?.found) toast.success(t('ADMIN_SIGNUPS.TOAST.SDK_FOUND'));
      else if (check?.error) toast.warning(t('ADMIN_SIGNUPS.TOAST.SDK_INCONCLUSIVE', { reason: t(`ADMIN_SIGNUPS.SDK.ERROR.${enumKey(check.error)}`, { defaultValue: check.error }) }));
      else toast.warning(t('ADMIN_SIGNUPS.TOAST.SDK_MISSING'));
      return detail || null;
    } catch {
      toast.error(t('ADMIN_SIGNUPS.TOAST.ACTION_FAILED'));
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
